// Pure pricing / assessment functions for Property Intelligence.
// Pricing is 100% derived from the existing estimator config + the SAME formula used by Estimate.js.
// No pricing is invented here; richer inputs only influence which REAL add-ons are suggested and the
// (clearly-labeled) complexity + rough duration guide computed from the customer's own selections.
import { NEEDS, DETAIL_LEVELS, CONDITION_STOPS, AREAS } from "./data";

export const round50 = (n) => Math.round((Number(n) || 0) / 50) * 50;

// Economy-of-scale taper (mirrors the backend): larger properties cost less per sqm.
export function effectiveSqm(cfg, sqm) {
  const n = Number(sqm) || 0;
  const t = (cfg && cfg.sqm_taper) || null;
  if (!t || !n) return n;
  const t1 = Number(t.tier1_sqm) || 0;
  const t2 = Number(t.tier2_sqm) || 0;
  const r1 = t.rate1 == null ? 1 : Number(t.rate1);
  const r2 = t.rate2 == null ? 1 : Number(t.rate2);
  const r3 = t.rate3 == null ? 1 : Number(t.rate3);
  if (t1 <= 0) return n;
  if (n <= t1) return n * r1;
  const eff = t1 * r1;
  if (t2 <= t1 || n <= t2) return eff + (n - t1) * r2;
  return eff + (t2 - t1) * r2 + (n - t2) * r3;
}

export const needByKey = (k) => NEEDS.find((n) => n.key === k) || NEEDS[0];
export const levelByKey = (k) => DETAIL_LEVELS.find((l) => l.key === k) || DETAIL_LEVELS[0];
export const conditionLabel = (k) => (CONDITION_STOPS.find((c) => c.key === k) || {}).label || k;

// Which areas are visible for the current profile.
export function visibleAreas(s) {
  return AREAS.filter((a) => a.show(s));
}

// Default REAL add-ons for a detail level, plus context-aware suggestions (all real add-ons).
export function defaultAddonsFor(levelKey, s) {
  const set = new Set(levelByKey(levelKey).addons);
  if (levelKey !== "essential") {
    if (s.balcony) set.add("balcony");
    if (s.constructionResidue) set.add("heavy_residue");
  }
  return [...set];
}

// EXACT same math as Estimate.js — range only, rounded to nearest 50.
// opts.tightened (2+ photos) narrows the uncertainty band around the SAME midpoint (higher confidence,
// not a different price). Never a guaranteed quote.
export function calcEstimate(cfg, s, addonIds, opts = {}) {
  if (!cfg || !s.estimatorKey || !cfg.base_per_sqm) return null;
  const per = cfg.base_per_sqm[s.estimatorKey] || 0;
  const min = (cfg.minimums && cfg.minimums[s.estimatorKey]) || 0;
  let base = Math.max(min, per * effectiveSqm(cfg, s.sqm));
  base *= (cfg.condition_multiplier && cfg.condition_multiplier[s.conditionKey]) || 1;
  base *= (cfg.furnishing_multiplier && cfg.furnishing_multiplier[s.furnishing]) || 1;
  base += (cfg.per_bathroom || 0) * (Number(s.bathrooms) || 0);
  const addTotal = (cfg.addons || [])
    .filter((a) => (addonIds || []).includes(a.id))
    .reduce((t, a) => t + (a.price || 0), 0);
  const total = base + addTotal;
  const baseSpread = cfg.range_spread || 0.15;
  const spread = opts.tightened ? Math.min(baseSpread, 0.1) : baseSpread;
  return {
    low: round50(total * (1 - spread)),
    high: round50(total * (1 + spread)),
    currency: cfg.currency || "\u20aa",
    tightened: !!opts.tightened,
  };
}

// Complexity is an assessment of the customer's OWN selections (clearly labeled as such).
export function complexity(s) {
  let score = 0;
  if (s.conditionKey === "Moderate") score += 1;
  if (s.conditionKey === "Heavy") score += 2;
  if (s.conditionKey === "Post-Construction") score += 3;
  if (s.constructionResidue) score += 2;
  if (s.windowComplexity === "Complex") score += 1;
  if (s.windowComplexity === "Very complex") score += 2;
  if (s.kitchenCondition === "Heavy") score += 1;
  if (s.bathroomCondition === "Heavy") score += 1;
  if (s.furnishing === "Furnished") score += 1;
  const sqm = Number(s.sqm) || 0;
  if (sqm >= 180) score += 2; else if (sqm >= 110) score += 1;
  if (Number(s.bathrooms) >= 3) score += 1;
  const level = score >= 6 ? "High" : score >= 3 ? "Medium" : "Low";
  const factors = [];
  if (s.conditionKey === "Heavy" || s.conditionKey === "Post-Construction") factors.push(conditionLabel(s.conditionKey) + " condition");
  if (s.constructionResidue) factors.push("Construction residue");
  if (s.windowComplexity && s.windowComplexity !== "Standard") factors.push(s.windowComplexity + " windows");
  if (s.furnishing === "Furnished") factors.push("Furnished");
  if (sqm >= 110) factors.push("Larger property");
  if (Number(s.bathrooms) >= 3) factors.push("Multiple bathrooms");
  return { score, level, factors };
}

// Rough duration GUIDE derived transparently from size + service intensity. Always labeled a guide.
const SERVICE_HOURS_FACTOR = {
  "General Cleaning": [0.03, 0.05],
  "General Deep Clean": [0.05, 0.08],
  "Move-In / Move-Out": [0.06, 0.09],
  "Post-Construction": [0.08, 0.12],
  "Property Detailing": [0.09, 0.14],
};
export function durationBand(s) {
  const f = SERVICE_HOURS_FACTOR[s.estimatorKey] || [0.05, 0.08];
  const sqm = Number(s.sqm) || 0;
  const mult = (s.furnishing === "Furnished" ? 1.1 : 1) *
    (s.conditionKey === "Heavy" || s.conditionKey === "Post-Construction" ? 1.15 : 1);
  let low = Math.max(3, Math.round(sqm * f[0] * mult));
  let high = Math.max(low + 1, Math.round(sqm * f[1] * mult));
  const band = high <= 5 ? "Half-day" : high <= 9 ? "Full-day" : "Multi-day";
  return { low, high, band };
}

// Build the full computed plan for the current selections + effective add-ons.
export function buildPlan(cfg, s) {
  const tightened = (Number(s.photoCount) || 0) >= 2;
  const est = calcEstimate(cfg, s, s.addons, { tightened });
  const cx = complexity(s);
  const dur = durationBand(s);
  const addonLabels = (cfg && cfg.addons ? cfg.addons : [])
    .filter((a) => (s.addons || []).includes(a.id))
    .map((a) => a.label);
  return { est, cx, dur, addonLabels };
}

// Build the customer-safe notes summary + the prefill object consumed by Contact.js (?prefill=intelligence).
export function buildPrefill(s, plan) {
  const need = needByKey(s.needKey);
  const level = levelByKey(s.detailLevel);
  const est = plan.est;
  const cur = est ? est.currency : "\u20aa";
  const lines = [
    "ASCENDIA PROPERTY INTELLIGENCE \u2014 self-assessment",
    `Recommended service: ${need.label}`,
    `Detail level: ${level.label}`,
    `Property: ${[s.propertyType, s.sqm && `${s.sqm} m\u00b2`, s.rooms && `${s.rooms} rooms`, s.bathrooms && `${s.bathrooms} baths`, s.furnishing].filter(Boolean).join(" \u00b7 ")}`,
    `Condition: ${conditionLabel(s.conditionKey)}`,
    `Complexity: ${plan.cx.level}${plan.cx.factors.length ? ` (${plan.cx.factors.join(", ")})` : ""}`,
    est ? `Estimated range (guide): ${cur}${est.low.toLocaleString()}\u2013${cur}${est.high.toLocaleString()}` : null,
    `Estimated duration (guide): ${plan.dur.low}\u2013${plan.dur.high} hrs (${plan.dur.band})`,
    plan.addonLabels.length ? `Selected detailing add-ons: ${plan.addonLabels.join(", ")}` : null,
    s.priorityAreas ? `Priority areas: ${s.priorityAreas}` : null,
    `Areas assessed: ${(s.areas || []).join(", ")}`,
  ].filter(Boolean);
  const notes = lines.join("\n");
  const propertyState = (s.constructionResidue || need.key === "post") ? "Post-construction" : "Previously lived in";
  const mediaList = (s.mediaList || []).map(({ path, url, original_filename, content_type }) => ({ path, url, original_filename, content_type }));
  const prefill = {
    service_required: need.leadLabel,
    property_type: s.propertyType || "",
    city: s.city || "",
    size_sqm: s.sqm ? String(s.sqm) : "",
    rooms: s.rooms ? String(s.rooms) : "",
    bathrooms: s.bathrooms ? String(s.bathrooms) : "",
    furnishing: s.furnishing || "",
    property_state: propertyState,
    deadline: s.deadlineDate || "",
    priority_areas: s.priorityAreas || plan.addonLabels.join(", "),
    notes,
    ai_summary: notes,
    ai_collected: {
      source: "property-intelligence",
      selections: s,
      estimate: est,
      complexity: plan.cx,
      duration: plan.dur,
      addons: plan.addonLabels,
      photos: mediaList.length,
      plan_token: s.planToken || null,
    },
    media: mediaList,
    // Shared property session — lets the Estimate page prefill from this plan (single source of truth).
    _estimate: {
      service: need.estimatorKey,
      sqm: s.sqm ? Number(s.sqm) : "",
      bathrooms: s.bathrooms ? Number(s.bathrooms) : 0,
      furnishing: s.furnishing || "Empty",
      condition: s.conditionKey || "Moderate",
      addons: s.addons || [],
    },
  };
  return { notes, prefill };
}

// WhatsApp message summarizing the plan (opens WhatsApp; does not auto-send).
export function buildWhatsAppText(s, plan) {
  const need = needByKey(s.needKey);
  const est = plan.est;
  const cur = est ? est.currency : "\u20aa";
  return [
    `Hi Ascendia, I built a Property Plan online.`,
    `Service: ${need.label} \u00b7 ${levelByKey(s.detailLevel).label}`,
    `${[s.sqm && `${s.sqm} m\u00b2`, s.rooms && `${s.rooms} rooms`, s.bathrooms && `${s.bathrooms} baths`].filter(Boolean).join(", ")}`,
    est ? `Estimated range (guide): ${cur}${est.low.toLocaleString()}\u2013${cur}${est.high.toLocaleString()}` : "",
    `Could we take the next step?`,
  ].filter(Boolean).join("\n");
}
